import { computed, ref, type Ref } from "vue";
import * as yup from "yup";
import { FormModes } from "@/types/form";
import {
  NationalIdNumberTypeIds,
  NationalIdNumberTypeDefinitions,
} from "@/types/application-details";
import type {
  FormChange,
  FormSchema,
  FormPatch,
  Option,
  FieldSchema,
} from "@/models/form";
import { FormPatchOperations } from "@/models/form";
import useValidation from "@/use/validation";
import useFormHelpers from "@/use/formHelpers";
import { ConsentServiceEntityTypeIds } from "@/types/consents";
import { useTypeStore } from "@/store/store/typeStore";
import { useForm } from "vee-validate";
import { getOptionLabel } from "@/composables/application/useApplicationForms";
import useAddressHelpers from "@/use/addressHelpers";
import useFormatter from "@/use/formatter";
import { $api } from "@/services/api1";
import type { StudentInformationFormVM } from "@/types/student";
import { Type, LOSType } from "@/types/types";
import useNameFieldValidationSchemas from "@/composables/schemas/useNameFieldValidationSchemas";

export interface ValidationData {
  studentInfo: Ref<Record<any, any>>;
  mode: Ref<keyof typeof FormModes>;
  entityTypeId: Ref<number>;
  useAddressInputComponent: boolean;
  addressSchema?: Ref<FormSchema | undefined>;
  addressValidationSchema?: Ref<yup.Schema | undefined>;
}

export function useStudentFormFields(data: ValidationData) {
  const typeStore = useTypeStore();

  const { removePrefix } = useAddressHelpers();
  const { formatDateOfBirth } = useFormatter();

  const {
    getErrorMessage,
    getNationalIdNumberErrorMessage,
    validItinRegex,
    validSSnRegex,
    validZipFormat1Regex,
    validZipFormat2Regex,
  } = useValidation();

  const { convertDateOfBirthToAge } = useFormHelpers();

  const nationalIdNumberLabel = computed<string>(() => {
    return data.studentInfo.value?.nationalIdNumberTypeId ==
      NationalIdNumberTypeIds.ITIN
      ? NationalIdNumberTypeDefinitions[NationalIdNumberTypeIds.ITIN]?.name
      : NationalIdNumberTypeDefinitions[NationalIdNumberTypeIds.SSN]?.name;
  });

  // #TODO
  const showAdditionalInfo = ref<boolean>(false);
  const confirmChangesTitle = ref<string>("Confirm");
  const errorModalTitle = ref<string>("Error");
  const studentInformationForm = ref<StudentInformationFormVM>();
  const showItinIndicator = ref<boolean>(false);

  const citizenshipStatusOptions = computed<Option[]>(() => {
    return studentInformationForm.value?.citizenshipStatusOptions &&
      studentInformationForm.value?.citizenshipStatusOptions?.length > 0
      ? studentInformationForm.value?.citizenshipStatusOptions
      : typeStore.getCitizenshipStatusOptions;
  });

  const addressUnitTypes = computed<LOSType[]>(() => {
    return studentInformationForm.value?.addressUnitTypes &&
      studentInformationForm.value?.addressUnitTypes?.length > 0
      ? studentInformationForm.value?.addressUnitTypes
      : typeStore.getAddressUnitTypes;
  });

  const unitTypeOptions = computed<Option[]>(() => {
    return studentInformationForm.value?.addressUnitTypeOptions &&
      studentInformationForm.value?.addressUnitTypeOptions?.length > 0
      ? studentInformationForm.value?.addressUnitTypeOptions
      : typeStore.getAddressUnitTypeOptions;
  });

  const statesOptions = computed<Option[]>(() => {
    return studentInformationForm.value?.stateOptions &&
      studentInformationForm.value?.stateOptions?.length > 0
      ? studentInformationForm.value?.stateOptions
      : typeStore.getStateOptions;
  });

  const countryOptions = computed<Option[]>(() => {
    return studentInformationForm.value?.countryOptions &&
      studentInformationForm.value?.countryOptions?.length > 0
      ? studentInformationForm.value?.countryOptions
      : typeStore.getCountryOptions;
  });

  const schema = ref<FormSchema>({
    firstName: {
      name: "firstName",
      type: "text",
      label: "First Name",
      placeholder: "First Name",
      required: true,
      maxLength: 32,
      validate: (schema: FieldSchema) => {
        schema.errors = errors.value?.firstName ? [errors.value.firstName] : [];
        schema.valid = schema.errors.length === 0;
        schema.touched = schema.errors.length > 0;
      },
    } as FieldSchema,
    lastName: {
      name: "lastName",
      type: "text",
      label: "Last Name",
      placeholder: "Last Name",
      required: true,
      maxLength: 32,
      validate: (schema: FieldSchema) => {
        schema.errors = errors.value?.lastName ? [errors.value.lastName] : [];
        schema.valid = schema.errors.length === 0;
        schema.touched = schema.errors.length > 0;
      },
    } as FieldSchema,
    dateOfBirth: {
      name: "dateOfBirth",
      label: "Date of Birth",
      type: "date",
      placeholder: "YYYY-MM-DD",
      required: true,
      validate: (schema: FieldSchema) => {
        schema.errors = errors.value?.dateOfBirth
          ? [errors.value.dateOfBirth]
          : [];
        schema.valid = schema.errors.length === 0;
        schema.touched = schema.errors.length > 0;
      },
    } as FieldSchema,
    citizenshipStatusId: {
      name: "citizenshipStatusId",
      type: "select",
      label: "Citizenship Status",
      required: true,
      tooltipLocation: "bottom",
      validate: (schema: FieldSchema) => {
        schema.errors = errors.value?.citizenshipStatusId
          ? [errors.value.citizenshipStatusId]
          : [];
        schema.valid = schema.errors.length === 0;
        schema.touched = schema.errors.length > 0;
      },
    } as FieldSchema,
    nationalIdNumber: {
      name: "nationalIdNumber",
      type: "nationalIdNumber",
      subtype: undefined,
      label: nationalIdNumberLabel.value,
      placeholder: "###-##-####",
      required: true,
      tooltipLocation: "bottom",
      errorMessage: `Please provide a valid ${nationalIdNumberLabel.value}`,
      showRequiredIndicator: true,
      validate: (schema: FieldSchema) => {
        schema.errors = errors.value?.nationalIdNumber
          ? [errors.value.nationalIdNumber]
          : [];
        schema.valid = schema.errors.length === 0;
        schema.touched = schema.errors.length > 0;
      },
    } as FieldSchema,
    parentConsent: {
      name: "parentConsent",
      type: "checkbox",
      label:
        "By checking this box, you affirm that you are the student's parent or legal guardian and that you claim the student as a dependent on your tax return.",
      errorMessage: "You must check the box to continue.",
      required:
        data.entityTypeId.value === ConsentServiceEntityTypeIds.Application,
      validate: (schema: FieldSchema) => {
        schema.errors = errors.value?.parentConsent
          ? [errors.value.parentConsent]
          : [];
        schema.valid = schema.errors.length === 0;
        schema.touched = schema.errors.length > 0;
      },
    } as FieldSchema,
  });

  const addressSchema = ref<FormSchema>({
    address1: {
      name: "address1",
      type: "text",
      subtype: "address",
      label: "Permanent Street Address",
      placeholder: "Street",
      required: true,
      maxLength: 64,
      validate: (schema: FieldSchema) => {
        schema.errors = errors.value?.address1 ? [errors.value.address1] : [];
        schema.valid = schema.errors.length === 0;
        schema.touched = schema.errors.length > 0;
      },
    } as FieldSchema,
    addressUnitTypeId: {
      name: "addressUnitTypeId",
      type: "select",
      subtype: "address",
      label: "Unit Type",
      required: false,
      validate: (schema: FieldSchema) => {
        schema.errors = errors.value?.addressUnitTypeId
          ? [errors.value.addressUnitTypeId]
          : [];
        schema.valid = schema.errors.length === 0;
        schema.touched = schema.errors.length > 0;
      },
    } as FieldSchema,
    address2: {
      name: "address2",
      type: "text",
      subtype: "address",
      label: "Unit Number",
      placeholder: "Unit #",
      required: false,
      maxLength: 64,
      validate: (schema: FieldSchema) => {
        schema.errors = errors.value?.address2 ? [errors.value.address2] : [];
        schema.valid = schema.errors.length === 0;
        schema.touched = schema.errors.length > 0;
      },
    } as FieldSchema,
    city: {
      name: "city",
      type: "text",
      label: "City",
      subtype: "address",
      placeholder: "City",
      required: true,
      maxLength: 32,
      validate: (schema: FieldSchema) => {
        schema.errors = errors.value?.city ? [errors.value.city] : [];
        schema.valid = schema.errors.length === 0;
        schema.touched = schema.errors.length > 0;
      },
    } as FieldSchema,
    stateId: {
      name: "stateId",
      type: "select",
      subtype: "address",
      label: "State",
      required: true,
      validate: (schema: FieldSchema) => {
        schema.errors = errors.value?.stateId ? [errors.value.stateId] : [];
        schema.valid = schema.errors.length === 0;
        schema.touched = schema.errors.length > 0;
      },
    } as FieldSchema,
    postalCode: {
      name: "postalCode",
      type: "zipcode",
      label: "Zip Code",
      placeholder: "Zip Code",
      errorMessage: "Zip code is required.",
      title: "Zip Code Format: XXXXX or XXXXX-XXXX or XXXXXXXXX",
      required: true,
      validate: (schema: FieldSchema) => {
        schema.errors = errors.value?.postalCode
          ? [errors.value.postalCode]
          : [];
        schema.valid = schema.errors.length === 0;
        schema.touched = schema.errors.length > 0;
      },
    } as FieldSchema,
    countryId: {
      name: "countryId",
      type: "select",
      label: "Country",
      errorMessage: "Country is required.",
      required: false,
      validate: (schema: FieldSchema) => {
        schema.errors = errors.value?.countryId ? [errors.value.countryId] : [];
        schema.valid = schema.errors.length === 0;
        schema.touched = schema.errors.length > 0;
      },
    } as FieldSchema,
  });

  const { firstNameValidationSchema, lastNameValidationSchema } =
    useNameFieldValidationSchemas(schema.value);

  const formFields = computed(() => {
    const fields = [
      {
        name: schema.value.firstName.name,
        label: schema.value.firstName.label,
        type: schema.value.firstName.type,
        required: schema.value.firstName.required,
        value: data.studentInfo.value?.firstName,
        width: "col-4",
        showOnAdd: true,
        showOnEdit: true,
        disabledOnEdit: false,
        visible: true,
        rules: firstNameValidationSchema,
      },
      {
        name: schema.value.lastName.name,
        label: schema.value.lastName.label,
        type: schema.value.lastName.type,
        required: schema.value.lastName.required,
        value: data.studentInfo.value?.lastName,
        width: "col-4",
        showOnAdd: true,
        showOnEdit: true,
        disabledOnEdit: false,
        visible: true,
        rules: lastNameValidationSchema,
      },
      {
        name: schema.value.dateOfBirth.name,
        label: schema.value.dateOfBirth.label,
        type: schema.value.dateOfBirth.type,
        required: schema.value.dateOfBirth.required,
        value: data.studentInfo.value?.dateOfBirth,
        width: "col-4",
        showOnAdd: true,
        showOnEdit: true,
        disabledOnEdit: false,
        visible: true,
      },
      {
        name: schema.value.citizenshipStatusId.name,
        label: schema.value.citizenshipStatusId.label,
        type: schema.value.citizenshipStatusId.type,
        required: schema.value.citizenshipStatusId.required,
        value: data.studentInfo.value?.citizenshipStatusId,
        width: "col-4",
        showOnAdd: true,
        showOnEdit: true,
        disabledOnEdit: false,
        options: citizenshipStatusOptions.value,
        visible: showAdditionalInfo.value,
      },
      {
        name: schema.value.nationalIdNumber.name,
        label: schema.value.nationalIdNumber.label,
        type: schema.value.nationalIdNumber.type,
        required: schema.value.nationalIdNumber.required,
        value: data.studentInfo.value?.nationalIdNumber,
        width: "col-4",
        showOnAdd: true,
        showOnEdit: true,
        disabledOnEdit: false,
        options: citizenshipStatusOptions.value,
        visible: showAdditionalInfo.value,
      },
    ];

    return fields.filter((field) => {
      if (data.mode.value === "add") {
        return field.showOnAdd !== false;
      } else if (data.mode.value === "edit") {
        return field.showOnEdit !== false;
      }

      return true;
    });
  });

  const addressFormFields = computed(() => {
    const fields = [
      {
        name: addressSchema.value.address1.name,
        label: addressSchema.value.address1.label,
        type: addressSchema.value.address1.type,
        required: addressSchema.value.address1.required,
        value: data.studentInfo.value?.address?.address1,
        width: "col-4",
        showOnAdd: true,
        showOnEdit: true,
        disabledOnEdit: false,
        visible: showAdditionalInfo.value,
      },
      {
        name: addressSchema.value.addressUnitTypeId.name,
        label: addressSchema.value.addressUnitTypeId.label,
        type: addressSchema.value.addressUnitTypeId.type,
        required: addressSchema.value.addressUnitTypeId.required,
        value: data.studentInfo.value?.address?.addressUnitTypeId,
        width: "col-4",
        showOnAdd: true,
        showOnEdit: true,
        disabledOnEdit: false,
        options: unitTypeOptions.value,
        visible: showAdditionalInfo.value,
      },
      {
        name: addressSchema.value.address2.name,
        label: addressSchema.value.address2.label,
        type: addressSchema.value.address2.type,
        required: addressSchema.value.address2.required,
        value: data.studentInfo.value?.address?.address2,
        width: "col-4",
        showOnAdd: true,
        showOnEdit: true,
        disabledOnEdit: false,
        visible: showAdditionalInfo.value,
      },
      {
        name: addressSchema.value.city.name,
        label: addressSchema.value.city.label,
        type: addressSchema.value.city.type,
        required: addressSchema.value.city.required,
        value: data.studentInfo.value?.address?.city,
        width: "col-4",
        showOnAdd: true,
        showOnEdit: true,
        disabledOnEdit: false,
        visible: showAdditionalInfo.value,
      },
      {
        name: addressSchema.value.stateId.name,
        label: addressSchema.value.stateId.label,
        type: addressSchema.value.stateId.type,
        required: true,
        value: data.studentInfo.value?.address?.stateId,
        width: "col-4",
        showOnAdd: true,
        showOnEdit: true,
        disabledOnEdit: false,
        options: statesOptions.value,
        visible: showAdditionalInfo.value,
      },
      {
        name: addressSchema.value.postalCode.name,
        label: addressSchema.value.postalCode.label,
        type: addressSchema.value.postalCode.type,
        required: addressSchema.value.postalCode.required,
        value: data.studentInfo.value?.address?.postalCode,
        title: "Zip Code Format: XXXXX or XXXXX-XXXX  or XXXXXXXXX",
        width: "col-4",
        showOnAdd: true,
        showOnEdit: true,
        disabledOnEdit: false,
        visible: showAdditionalInfo.value,
      },
      {
        name: addressSchema.value.countryId.name,
        label: addressSchema.value.countryId.label,
        type: addressSchema.value.countryId.type,
        required: addressSchema.value.countryId.required,
        value: data.studentInfo.value?.address?.countryId,
        width: "col-4",
        showOnAdd: false,
        showOnEdit: false,
        disabledOnEdit: false,
        options: countryOptions.value,
        visible: false,
      },
    ];

    return fields.filter((field) => {
      if (data.mode.value === "add") {
        return field.showOnAdd !== false;
      } else if (data.mode.value === "edit") {
        return field.showOnEdit !== false;
      }

      return true;
    });
  });

  const addressValidationSchema = computed<yup.Schema>(() => {
    return yup.object().shape({
      address1: yup
        .string()
        .label(
          addressSchema.value.address1?.label || "Permanent Street Address"
        )
        .transform((curr, orig) => (orig === "" ? undefined : curr))
        .required(
          getErrorMessage(
            data.addressSchema?.value?.address1 || addressSchema.value.address1
          )
        )
        .test(
          "valid-input",
          `${addressSchema.value.address1.label} can only contain letters, numbers, apostrophes, hyphens, and spaces.`,
          function (value) {
            // Only allow standard A-Z a-z 0-9 values, single space, and - '
            const valid = /^[A-Za-z0-9-' ]+$/.test(value);

            return valid === true;
          }
        ),
      addressUnitTypeId: yup
        .number()
        .label(addressSchema.value?.addressUnitTypeId?.label || "Unit Type")
        .transform((value) => (isNaN(value) ? undefined : Number(value)))
        .test(
          "is-valid",
          getErrorMessage(
            data.addressSchema?.value?.addressUnitTypeId ||
              addressSchema.value?.addressUnitTypeId
          ),
          function (value, testContext) {
            const address2 = testContext.parent.address2;

            if (!address2) return true;

            if (
              address2 !== undefined &&
              address2 !== null &&
              (address2.toString() || "")?.trim()?.length > 0
            ) {
              return !isNaN(Number(value)) && Number(value) > 0;
            }

            return false;
          }
        ),
      address2: yup
        .string()
        .label(addressSchema.value.address2?.label || "Unit Number")
        .nullable()
        .transform((curr, orig) => (orig === "" ? undefined : curr))
        .test(
          "valid-unit-number",
          getErrorMessage(
            data.addressSchema?.value?.address2 || addressSchema.value.address2
          ),
          function (value, testContext) {
            const unitType = testContext.parent.addressUnitTypeId;
            const hasInput =
              value !== undefined && value !== null && value?.trim().length > 0;

            if (!unitType && !hasInput) return true;

            if (
              hasInput ||
              (unitType !== undefined &&
                unitType !== null &&
                !isNaN(Number(unitType)) &&
                Number(unitType) > 0)
            ) {
              if (!hasInput) {
                return testContext.createError({
                  message: `${addressSchema.value.address2.label} is a required field.`,
                });
              }
              // Only allow standard A-Z a-z 0-9 values and single space
              const specialCharacterCheck = /[^A-Za-z0-9\s]+$/.test(
                value ?? ""
              );

              if (specialCharacterCheck) {
                return testContext.createError({
                  message: `${addressSchema.value.address2.label} can only contain letters, numbers, and spaces.`,
                });
              }

              return true;
            }

            return false;
          }
        ),
      city: yup
        .string()
        .label(addressSchema.value.city?.label || "City")
        .required(
          getErrorMessage(
            data.addressSchema?.value?.city || addressSchema.value.city
          )
        )
        .max(40)
        .transform((curr, orig) => (orig === "" ? undefined : curr))
        .test(
          "valid-input",
          `${addressSchema.value.city.label} can only contain letters, apostrophes, hyphens, and spaces.`,
          function (value) {
            // Only allow standard A-Z a-z values, apostrophes, single space, and - '
            const invalidCharCheck = /[^A-Za-z-' ]/.test(value);
            return invalidCharCheck === false;
          }
        ),
      stateId: yup
        .number()
        .label(addressSchema.value.stateId?.label || "State")
        .transform((value) => (isNaN(value) ? undefined : Number(value)))
        .required(
          getErrorMessage(
            data.addressSchema?.value?.stateId || addressSchema.value.stateId
          )
        )
        .min(
          1,
          getErrorMessage(
            data.addressSchema?.value?.stateId || addressSchema.value.stateId
          )
        ),
      postalCode: yup
        .string()
        .label(addressSchema.value.postalCode?.label || "Zip")
        .required(
          getErrorMessage(
            data.addressSchema?.value?.postalCode ||
              addressSchema.value.postalCode
          )
        )
        .test(
          "is-valid-zip",
          "Please enter a valid ZIP code in 5-digit or 9-digit format (e.g., 12345, 12345-6789, or 123456789).",
          (value) => {
            // Handles 12345, 12345-6789
            const formatCheck1 = validZipFormat1Regex.test(value);
            // Handles 123456789
            const formatCheck2 = validZipFormat2Regex.test(value);

            if (value === undefined) {
              return false;
            } else if (value.length === 5 && formatCheck1) {
              return true;
            } else if (value.length === 10 && formatCheck1) {
              return true;
            } else if (value.length === 9 && formatCheck2) {
              return true;
            } else {
              return false;
            }
          }
        ),
    });
  });

  const validationSchema = computed<yup.Schema>(() => {
    return yup.object().shape({
      firstName: firstNameValidationSchema,
      lastName: lastNameValidationSchema,
      citizenshipStatusId: yup
        .number()
        .label(schema.value.citizenshipStatusId?.label || "Citizenship Status")
        .transform((value) => (isNaN(value) ? undefined : Number(value)))
        .required(getErrorMessage(schema.value.citizenshipStatusId))
        .min(1, getErrorMessage(schema.value.citizenshipStatusId)),
      nationalNumberTypeId: yup.number().notRequired(),
      nationalIdNumber: yup
        .string()
        .transform((curr, orig) =>
          orig === "" || orig === undefined || orig === null ? undefined : curr
        )
        .test("valid-itin", "Please provide a valid ITIN.", (value) => {
          if (
            !isNaN(Number(data.studentInfo.value.nationalIdNumberTypeId)) &&
            data.studentInfo.value.nationalIdNumberTypeId &&
            Number(data.studentInfo.value.nationalIdNumberTypeId) ===
              Number(NationalIdNumberTypeIds.ITIN)
          ) {
            if (!value) {
              return false;
            }
            // Follows XXX-XX-#### format
            const masked = /^X{3}-X{2}-\d{4}$/.test(value);
            if (masked) {
              return true;
            }
            const validITIN = validItinRegex.test(value);

            return validITIN;
          }
          return true;
        })
        .test(
          "valid-ssn",
          "Please provide a valid SSN.",
          async (value: string | undefined) => {
            if (
              !isNaN(Number(data.studentInfo.value.nationalIdNumberTypeId)) &&
              data.studentInfo.value.nationalIdNumberTypeId &&
              Number(data.studentInfo.value.nationalIdNumberTypeId) ===
                Number(NationalIdNumberTypeIds.SSN)
            ) {
              if (!value) {
                return false;
              }

              // Follows XXX-XX-#### format
              const masked = /^X{3}-X{2}-\d{4}$/.test(value);
              if (masked) {
                return true;
              }

              const validSSN = validSSnRegex.test(value);

              return validSSN;
            }
            return true;
          }
        ),
      dateOfBirth: yup
        .string()
        .label(schema.value.dateOfBirth.label || "Date of Birth")
        .transform((curr, orig) => (orig === "" ? undefined : curr))
        .required(`${schema.value.dateOfBirth.label} is required`)
        .test(
          "is-valid-ISO8601-format",
          `Please provide a valid ${schema.value.dateOfBirth.label}`,
          (value) => {
            const validISO8601Date =
              /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/.test(value);
            if (validISO8601Date) {
              const age = convertDateOfBirthToAge(value);
              if (age && age > 0 && age <= 150) {
                return true;
              }
            }

            return false;
          }
        ),
      parentConsent: yup
        .boolean()
        .test(
          "is-valid",
          `${getErrorMessage(schema.value.parentConsent)}`,
          function (value) {
            if (!schema.value.parentConsent.required) {
              return true;
            }

            return value !== undefined && value !== null && value !== false;
          }
        ),
      address:
        data?.useAddressInputComponent === true
          ? yup.object({})
          : addressValidationSchema.value,
    });
  });

  const { values, errors, setFieldValue, setValues, validateField } = useForm({
    validationSchema,
  });

  function changeMode(newMode: keyof typeof FormModes) {
    data.mode.value = newMode;
  }

  function handleFieldChange(fieldName: string, value: any) {
    setFieldValue(fieldName, value);
    switch (fieldName) {
      case "citizenshipStatusId":
        data.studentInfo.value.citizenshipStatusName =
          getOptionLabel(citizenshipStatusOptions.value, Number(value)) || "";
        break;
      case "stateId":
        data.studentInfo.value.address.stateName =
          getOptionLabel(statesOptions.value, Number(value)) || "";
        break;
      case "countryId":
        data.studentInfo.value.address.countryName =
          getOptionLabel(countryOptions.value, Number(value)) || "";
        break;
      case "nationalIdNumber":
        data.studentInfo.value.nationalIdNumber = value;
        break;
      case "itinIndicator":
        data.studentInfo.value.itinIndicator = value;
        data.studentInfo.value.nationalIdNumberTypeId =
          value === true
            ? NationalIdNumberTypeIds.ITIN
            : NationalIdNumberTypeIds.SSN;
        break;
      default:
        return;
    }
  }

  function formatAddress2(
    addressUnitTypeId: number | null,
    address2: string | null
  ) {
    const selectedUnitTypeId = addressUnitTypeId
      ? Number(addressUnitTypeId)
      : null;

    const selectedUnitType = selectedUnitTypeId
      ? addressUnitTypes.value?.find(
          (a: LOSType) => a.id === selectedUnitTypeId
        )
      : null;

    const prefixes = [] as string[];

    addressUnitTypes.value.forEach((au: any) => {
      prefixes.push(au?.abbreviation);
      prefixes.push(au?.name);
    });

    return selectedUnitTypeId
      ? `${selectedUnitType?.abbreviation} ${removePrefix(
          address2?.trim(),
          prefixes
        )}`
      : null;
  }

  async function getStudentInformationFormAsync(referenceId: string) {
    const response = await $api.applications.getStudentInformationForm(
      referenceId,
      data.entityTypeId.value
    );

    if (response && response?.success === true) {
      studentInformationForm.value = response.results;

      if (
        studentInformationForm.value?.addressUnitTypes &&
        studentInformationForm.value?.addressUnitTypes?.length > 0
      ) {
        typeStore.types[Type.AddressUnits] =
          studentInformationForm.value?.addressUnitTypes;
      }

      if (
        studentInformationForm.value?.countries &&
        studentInformationForm.value?.countries?.length > 0
      ) {
        typeStore.types[Type.Countries] =
          studentInformationForm.value?.countries;
      }

      if (
        studentInformationForm.value?.states &&
        studentInformationForm.value?.states?.length > 0
      ) {
        typeStore.types[Type.States] = studentInformationForm.value?.states;
      }
    }
  }

  function generatePatchPath(pathArray: string[]) {
    return "/" + pathArray.join("/");
  }

  function generatePathOp(
    before: any,
    after: any
  ): keyof typeof FormPatchOperations {
    const op = FormPatchOperations.replace;

    if (before === null && after !== null) {
      return "add";
    } else if (before !== null && after === null) {
      return "remove";
    } else {
      return op;
    }
  }

  function buildStudentFormChangesTable(changes: FormChange[]) {
    let html = "";

    changes.forEach((change) => {
      html += `<tr><td class="xbefore" style="font-size: 14px;font-weight: 600;color:#000;border-left: none; line-height: normal;">${
        change.name || change.label
      }</td><td class="xafter" style="font-weight: 400;font-size: 14px;">${
        change.before
      }</td><td class="xafter" style="font-weight: 400;font-size: 14px;line-height: normal;">${
        change.after
      }</td></tr>`;
    });

    return html?.length > 0
      ? `<table class='xmodal-changes' id='student-form'">
		<thead>
		<tr>
		<th></th>
		<th style="text-transform: uppercase;text-align: left;font-size: 12px;font-weight:600;">Previous</th>
		<th style="color: #48A842;text-transform: uppercase;text-align: left;font-size: 12px;font-weight:600;">New</th>
		</tr>
		</thead>
		<tbody>${html}</tbody>
		</table>`
      : null;
  }
  return {
    addressFormFields,
    addressSchema,
    addressValidationSchema,
    addressUnitTypes,
    buildStudentFormChangesTable,
    changeMode,
    citizenshipStatusOptions,
    confirmChangesTitle,
    countryOptions,
    errorModalTitle,
    errors,
    formFields,
    formatDateOfBirth,
    formatAddress2,
    generatePatchPath,
    generatePathOp,
    getStudentInformationFormAsync,
    handleFieldChange,
    nationalIdNumberLabel,
    removePrefix,
    schema,
    showItinIndicator,
    setFieldValue,
    setValues,
    statesOptions,
    studentInformationForm,
    typeStore,
    validationSchema,
    values,
    validateField,
  };
}
